import React, { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '@/data/models/album';
import {
  ErrorType,
  IValidationResults,
  ValidationMetadata,
} from '@/data/models/validation';
import { parseDateString, parseDate } from '@/utils/helper';
import DatePicker from '../../../common/DatePicker/DatePicker';
import { FormGroupProps } from './types';
import useAuthUtilities from '../../../../AuthMiddleware/AuthUtilities';
import ReadOnlyField from '@/components/common/ReadOnlyField/ReadOnlyField';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';

type ReleaseDateFormGroupProps = FormGroupProps & {
  albumValidationMetadata: ValidationMetadata | undefined;
  showValidationErrors: boolean;
  albumErrors: IValidationResults[];
  onChange: (releaseDate: string | null) => void;
};

export default function ReleaseDateFormGroup({
  albumValidationMetadata,
  album,
  readonly,
  disabled,
  showValidationErrors,
  albumErrors,
  onChange,
}: ReleaseDateFormGroupProps) {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [releaseDateChanged, setReleaseDateChanged] = useState(false);

  const onDateChange = (date: Date | [Date, Date] | null) => {
    setReleaseDateChanged(true);

    if (Array.isArray(date)) {
      date = date[0];
    }

    onChange(parseDateString(date));
  };

  const minimumReleaseDate = getMinimumReleaseDate(
    albumValidationMetadata,
    album
  );

  const releaseDate = parseDate(album.releaseDate);

  const releaseDateIssue = albumErrors.find(
    (va) => va.fieldName === 'ReleaseDate'
  );

  const releaseDateIsMissing =
    releaseDateIssue !== undefined &&
    releaseDateIssue.errorType !== ErrorType.InvalidValue &&
    !releaseDateChanged;

  const releaseDateViolatesMinimum =
    (minimumReleaseDate && releaseDate && releaseDate < minimumReleaseDate) ??
    false;

  const releaseDateIsInvalid =
    releaseDateViolatesMinimum ||
    (releaseDateIssue?.errorType === ErrorType.InvalidValue &&
      !releaseDateChanged);

  const isInvalid =
    showValidationErrors &&
    ((releaseDateIsMissing || releaseDateIsInvalid) ?? false);
  const issueStyleClass =
    releaseDateIssue?.isValid === VALIDATION_STATE_WARNING
      ? 'warning-input'
      : 'invalid-input';
  const validationClassName = isInvalid ? issueStyleClass : '';
  const inputClassName = validationClassName
    ? `form-control ${validationClassName}`
    : 'form-control';

  return (
    <>
      {!readonly ? (
        <div className="form-group release-date-form-group">
          <DatePicker
            id="releaseDate"
            labelClassName="col-sm-3 form-label"
            label={t('product.editProduct.releaseDate')}
            className={inputClassName}
            calendarClassName={validationClassName}
            value={releaseDate}
            onChange={onDateChange}
            disabled={disabled}
            minDate={minimumReleaseDate}
            openToDate={minimumReleaseDate}
            onCalendarClose={() => setCalendarOpen(false)}
            onCalendarOpen={() => setCalendarOpen(true)}
            onChangeRaw={() => setReleaseDateChanged(true)}
          />

          <ValidationPopup
            releaseDateIsMissing={releaseDateIsMissing}
            releaseDateIsInvalid={releaseDateIsInvalid}
            validationMessage={releaseDateIssue?.validationMessage}
            validationState={releaseDateIssue?.isValid}
            showValidationErrors={showValidationErrors}
            calendarOpen={calendarOpen}
            vmgManufacturing={
              album.albumMetaData?.manufacturing?.isManufacturedByIngrooves ===
              true
            }
          />
        </div>
      ) : (
        <ReadOnlyField
          id={'releaseDate'}
          labelTitle={t('product.editProduct.releaseDate')}
          value={album.releaseDate}
        ></ReadOnlyField>
      )}
    </>
  );
}

export function ValidationPopup({
  releaseDateIsMissing,
  releaseDateIsInvalid,
  validationMessage,
  validationState,
  showValidationErrors,
  calendarOpen,
  vmgManufacturing,
}: {
  releaseDateIsMissing: boolean;
  releaseDateIsInvalid: boolean;
  validationMessage?: string;
  validationState?: string;
  showValidationErrors: boolean;
  calendarOpen: boolean;
  vmgManufacturing: boolean;
}) {
  const { t } = useTranslation();
  const { isInternalUser } = useAuthUtilities();
  const hasReleaseDateError =
    showValidationErrors && (releaseDateIsMissing || releaseDateIsInvalid);

  const tooltipText = vmgManufacturing
    ? t('tooltip.minimumReleaseDate.vmgManufacturingTrue')
    : t('tooltip.minimumReleaseDate.vmgManufacturingFalse');

  if (!calendarOpen) {
    return null;
  }

  if (hasReleaseDateError) {
    const errorMessage =
      validationMessage ||
      (releaseDateIsMissing
        ? t(
            'validationAlert.productEditMode.ReleaseDate.NullorEmpty.ErrorMessage'
          )
        : t(
            'validationAlert.productEditMode.ReleaseDate.InvalidValue.ErrorMessage'
          ));

    const className =
      validationState === VALIDATION_STATE_WARNING
        ? 'col-sm-2 popUp-date-container-warning release-date-error-popup'
        : 'col-sm-2 popUp-date-container release-date-error-popup';

    return <div className={className}>{errorMessage}</div>;
  }

  if (isInternalUser()) {
    return null;
  }

  return (
    <div className="col-sm-3 popUp-date-container-tooltip">{tooltipText}</div>
  );
}

function getMinimumReleaseDate(
  validationMetadata: ValidationMetadata | undefined,
  album: IAlbum | undefined
): Date | undefined {
  const manufacturedByVmg =
    album?.albumMetaData?.manufacturing?.isManufacturedByIngrooves == true;

  return validationMetadata
    ? moment(
        manufacturedByVmg
          ? validationMetadata.minimumReleaseDateVmgManufacturing
          : validationMetadata.minimumReleaseDate,
        'YYYY-MM-DD'
      ).toDate()
    : undefined;
}
